import { ViewType, ViewObject } from '../base';

import { AppConstants } from '@unpispas/upp-defs';
import { PriceType, PriceInfo } from '@unpispas/upp-defs';

import { dataService } from '../../data';
import { Preselect } from '../objects/preselect';
import { ProductOpt } from '../objects/productopt';

import { ProductView } from './product';
import { ProductOptView } from './productopt';
import { PlaceView } from './place';
import { QrCodeView } from './qrcode';
import { PreselectOpt } from '../objects/preselectopt';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _PreselectView extends ViewObject<Preselect> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get preselect(): Preselect {
        return this.object;
    }
             
    constructor(object: Preselect, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _preselectprice: number | null = null;
    private _validoptions: Array <ProductOptView> | null = null;
    private _calcpriceinfo: PriceType | null = null;
    private _isinvalid: boolean | null = null;
    private _iscompleted: boolean | null = null;
    
    private RefreshView(targets: string[]){
        if (targets.includes('PRESELECT')){
            this._isinvalid = null;     
            this.DoRefreshView();
        }

        if (targets.includes('CATEGORY')){
            this._isinvalid = null;     
            this.DoRefreshView();
        }

        if (targets.includes('PRODUCTCHECKED')){
            this.DoRefreshView();
        }

        if (targets.includes('PRODUCTCHANGED')){
            this._preselectprice = null;
            this.DoRefreshView();
        }

        if (targets.includes('OPTIONCHECKED')){
            this._iscompleted = null; 
            this.DoRefreshView();
        }        

        if (targets.includes('OPTIONCHANGED')){
            this._isinvalid = null;     
            this._preselectprice = null;
            this.DoRefreshView();
        }        

        if (targets.includes('DEPENDCHANGED')){
            this._isinvalid = null;     
            this.DoRefreshView();
        }        

        if (targets.includes('PRESELECTOPT')){
            this._preselectprice = null;
            this._validoptions = null;
            this.DoRefreshView();
        }

        if (targets.includes('PRODUCTOPT')){
            this._isinvalid = null;     
            this._preselectprice = null;
            this._validoptions = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

        // update registrations when children change   
        this._subscriptions.push(
            this.preselect.OnChildChanged.subscribe(() => {
                const self = weakref.deref();
                if (self) self.doRegister();
            })
        );

        // register to updates on contained items         
        this._subscriptions.push(
            this.preselect.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }                                
            })
        )    

        const _product = this.product;
        if (_product){
            this._subscriptions.push(
                _product.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }                
                })    
            );
        }
    }

    /**********************************/
    /* ADD / REMOVE CHILDREN          */
    /**********************************/

    private GetOption(option: ProductOpt): PreselectOpt | null {
        for (const _preselectopt of this.preselect.options){
            if (_preselectopt.IsValid && (_preselectopt.option == option)){
                return _preselectopt;
            }
        }

        return null;    // not found
    }

    AddOption(option: ProductOpt): void {
        if (!option || (this.GetOption(option) != null)){
            return;
        }

        const _preselectopt = new PreselectOpt(null, this.data);
        if (_preselectopt){
            _preselectopt.option = option;
            _preselectopt.select = this.preselect;
            _preselectopt.status = 'AC';

            this.preselect.AddOption(_preselectopt);
        }
    }

    DelOption(option: ProductOpt): void {
        if (!option){
            return;
        }
        
        const _preselectopt = this.GetOption(option);
        if (_preselectopt){
            _preselectopt.status = 'DE';
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/

    get product(): ProductView | null {
        return this.data.alives.get(this.preselect.product) as ProductView;
    }

    get options(): Array <ProductOptView> {
        if (!this.product){
            return [];
        }

        if (this._validoptions === null){
            const _options: Array <ProductOptView> = []; 

            for(const _select of this.preselect.options){
                if (_select && _select.IsValid){
                    const _option: ProductOptView = this.data.alives.get(_select.option) as ProductOptView;
                    if (_option){
                        _options.push(_option);
                    }
                }
            }

            // sort the options (by category and order inside the category)
            _options.sort((a, b) => {
                const _a_ctgr = a.category?.sort || 0;
                const _b_ctgr = b.category?.sort || 0;

                if (_a_ctgr !== _b_ctgr){
                    return _a_ctgr - _b_ctgr;
                }

                const _a_sort = a.sort || 0;
                const _b_sort = b.sort || 0;

                return _a_sort - _b_sort;
            });

            this._validoptions = _options;
        }

        return this._validoptions;
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/

    private _checked = false;
    get Checked(): boolean {
        return this._checked;
    }

    set Checked(value: boolean){
        this._checked = value;
        this.preselect.DoRefresh('PRODUCTCHECKED');
    }

    get IsAbleToCart(): boolean {
        if (this.preselect.IsMarket || this.preselect.IsWeighted){
            return false;
        }

        return true;
    }    

    get price(): number {
        if (this._preselectprice === null){
            let _price = this.preselect.product?.price || 0;
            for (const _option of this.options){
                _price += _option.price || 0;
            }
            this._preselectprice = _price;
        }

        return this._preselectprice ;   
    }

    get priceinfo(): PriceType | null {
        if (this.preselect.IsMarket){
            return null;    // no priceinfo is provided
        }

        if ((this._preselectprice === null) || (this._calcpriceinfo == null)) {
            const _p: number = this.price;
            const _r: number = this.preselect.product?.taxrate ?? AppConstants.defaultTaxRate;
    
            this._calcpriceinfo = new PriceInfo(_p, _r).price;
        }

        return this._calcpriceinfo;
    }

    /**
     * Shortcut charge at the ticket's place/table: product `tableprice` plus each linked
     * option's `tableprice` (same zone/period resolution as ticket lines).
     */
    tableprice(place: PlaceView | null, qrcode: QrCodeView | null, at: Date = new Date()): number {
        let _price = this.product?.tableprice(place, qrcode, at) ?? 0;

        for (const _option of this.options){
            _price += _option.tableprice(place, qrcode, at);
        }

        return _price;
    }

    /**
     * True when the parent product is valid and no active shortcut link points to a deleted product option.
     */
    get IsValid(): boolean {
        if (!this.preselect.status || this.preselect.status === 'DE'){
            return false;
        }

        if (!this.product || !this.product.IsValid){
            return false;
        }

        for (const _select of this.preselect.options){
            if (_select.IsValid && _select.option && !_select.option.IsValid){
                return false;
            }
        }

        return true;
    }

    /**
     * Determines if the product and the options are available
     */
    get IsAvailable(): boolean {
        if (!this.IsValid || !this.product?.IsAvailable){
            return false;
        }

        const _options = this.options;
        if (!_options.every(option => option.IsAvailable)){
            return false;
        }

        for (const _option of _options){
            const _category = _option.category;
            if (!_category){
                return false;
            }

            const _found = this.product.categories.some(
                (category) => category === _category
            );
            if (!_found){
                return false;
            }
        }

        return true;
    }

    /**
     * Determines if the shortcut has been wrongly configured
     */
    get IsInvalid(): boolean {
        if (this._isinvalid === null){
            this._isinvalid = this._IsInvalid;
        }

        return this._isinvalid;
    }

    /**
     * Determines if the shortcut can be accepted during edition
     */
    get IsCompleted(): boolean {
        if (this._iscompleted === null){
            this._iscompleted = this._IsCompleted;
        }

        return this._iscompleted;
    }

    /**********************************/
    /* PRIVATE METHODS                */
    /**********************************/

    private get _IsInvalid(): boolean {
        let _valid = false;

        if (!this.product){
            return true;
        }

        if (this.product.options.length == 0){
            return true;   // no options in this product
        }

        // we mark the shortcut options
        for(const _option of this.options){
            _option.Checked = true;
        }

        // we test completion
        _valid = this._IsCompleted;

        // we reset the options selection
        this.product.ResetChecked();

        return (_valid == false);
    }

    private get _IsCompleted(): boolean {
        if (!this.product){
            return false;
        }

        if (this.product.options.length == 0){
            return false;   // no options in this product
        }

        let _completed = true;

        for (const _category of this.product.categories){
            if (!_category.Suitable){
                continue;
            }

            if (_category.type == 'SINGLE'){
                const _size: number = _category.Selected.length;
                _completed = _completed && (_size == 1)
            }

            if (_category.type == 'FIXED'){
                const _size: number = _category.Selected.length;
                _completed = _completed && (_size >= (_category.fxmin || 0)) && (_size <= (_category.fxmax || 0));
            }

            if (!_completed){
                return false;
            }
        }

        return true;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type PreselectView = ViewType <_PreselectView, Preselect>;

export function PreselectViewProxy(object: Preselect, data: dataService): PreselectView {
    return new _PreselectView(object, data).Proxy as PreselectView;
}
